import $ from "jquery";
import logger from "../common/Logger"

class ConfigPhase {
    static UNINITIALISED = 'uninitialised';
    static INITIALISING = 'initialising';
    static INITIALISED = 'initialised';
}

class ConfigConstants {
    /**
     * The API base URL.
     *
     * @type {string}
     */
    static apiBaseUrl = 'apiBaseUrl';

    /**
     * The Stripe publishable key.
     *
     * @type {string}
     */
    static stripeKey = 'stripeKey';

    /**
     * The Auth0 client ID.
     *
     * @type {string}
     */
    static auth0ClientId = 'auth0ClientId';

    /**
     * The default product ID.
     *
     * @type {string}
     */
    static defaultProductId = 'defaultProductId';
}

class ConfigService {
    _phase = ConfigPhase.UNINITIALISED;
    _state = {};
    marketingHomeUrl = 'https://www.mocks.cloud';

    /**
     * Callbacks for when config has been initialised successfully.
     *
     * @type {Array}
     */
    readyListeners = [];

    constructor() {
        this.fetchConfiguration = this.fetchConfiguration.bind(this);
        this._onReady = this._onReady.bind(this);
        this.onReadyOneShot = this.onReadyOneShot.bind(this);
        this.notifyReady = this.notifyReady.bind(this);
        this.getItem = this.getItem.bind(this);
        this._unsafeGetItem = this._unsafeGetItem.bind(this);
    }

    /**
     * Indicates whether the config has been initialised successfully.
     *
     * @type {string}
     */
    get phase() {
        return this._phase;
    }

    fetchConfiguration() {
        const that = this;

        this._phase = ConfigPhase.INITIALISING;

        // raw AJAX call; don't use ApiService to avoid infinite recursion
        $.ajax('/assets/web-config.json', {
            type: 'GET',
            success: function (content) {
                logger.debug('Retrieved web configuration');
                that._state = (typeof content === 'object' ? content : JSON.parse(content));
                that._phase = ConfigPhase.INITIALISED;
                that.notifyReady()
            },
            error: function (err) {
                logger.error('Failed to retrieve web configuration', err);
            }
        });
    }

    /**
     * Register the callback, which will not be removed after invocation.
     *
     * @param callback {Function}
     */
    _onReady(callback) {
        this.readyListeners.push(callback);
    }

    /**
     * Register the callback, which will be removed after invocation.
     *
     * @param callback {Function}
     */
    onReadyOneShot(callback) {
        const that = this;
        this._onReady(function () {
            const wrappingCallback = this;
            that.readyListeners = that.readyListeners.filter(e => e !== wrappingCallback);
            callback();
        });
    }

    notifyReady() {
        logger.debug(`Notifying ${this.readyListeners.length} config ready listeners`);
        this.readyListeners.forEach((listener) => {
            // bind 'this' to self
            listener.bind(listener)();
        });
    }

    /**
     * Get the item from configuration and pass it to the callback function.
     *
     * @param itemName {String}
     * @param callback {Function}
     */
    getItem(itemName, callback) {
        const invokeCallbackOnReady = (logMessage) => {
            logger.debug(logMessage);
            this.onReadyOneShot(() => callback(this._unsafeGetItem(itemName)));
        };

        switch (this.phase) {
            case ConfigPhase.UNINITIALISED:
                invokeCallbackOnReady(`Configuration not yet retrieved - fetching before reading item: ${itemName}`);
                this.fetchConfiguration();
                break;

            case ConfigPhase.INITIALISING:
                invokeCallbackOnReady(`Configuration already being retrieved - awaiting callback before reading item: ${itemName}`);
                break;

            case ConfigPhase.INITIALISED:
                callback(this._unsafeGetItem(itemName));
                break;

            default:
                logger.error(`Unknown config phase: ${this.phase}`);
        }
    }

    /**
     * Get the item from the state. Only safe to call if phase === INITIALISED.
     *
     * @param itemName {String}
     */
    _unsafeGetItem(itemName) {
        return this._state[itemName];
    }
}

export default new ConfigService();
export {ConfigPhase};
export {ConfigConstants};



// WEBPACK FOOTER //
// ./src/modules/service/ConfigService.js